import React, { FunctionComponent, useState, useCallback, KeyboardEvent, useEffect } from 'react';
import { palettes, EllipsisLoader, SelectOption } from '@indigo-ag/component-module';
import debounce from 'lodash/debounce';

import { createStylesHook } from 'shared/hooks';
import { KEY_PRESSED } from 'shared/constants';
import { SHOW_SUGGESTIONS_AFTER, SUGGESTIONS_LIMIT, DEFAULT_EXECUTION_DELAY } from './constants';
import i18next from 'i18next';

interface AutocompleteProps {
  id: string;
  value: string;
  label: string;
  onChange?: (hint: string) => any;
  isLoading?: boolean;
  suggestions: Array<SelectOption>;
  onSelectOption: (option: SelectOption) => any;
  showSuggestionsAfter?: number;
  suggestionsLimit?: number;
  reset?: boolean;
}

const useStyles = createStylesHook(() => ({
  layout: {
    transitionProperty: 'color,background-color,box-shadow',
    border: '1px solid',
    borderColor: palettes.brand.gray['500'],
    borderRadius: '4px',
    backgroundColor: palettes.neutral.white,
    padding: '7px 15px',
    '&:hover': {
      borderColor: palettes.brand.gray['600'],
      boxShadow: '0px 1px 2px rgba(0,87,125,0.14), 0px 1px 1px rgba(0,87,125,0.1)'
    },
    '&:focus-within': {
      borderColor: palettes.brand.gray['600'],
      boxShadow: '0 0 0 4px #A8DBE5',
      outline: 'none'
    },
    marginBottom: '10px'
  },
  suggestionsList: {
    backgroundColor: palettes.neutral.white,
    borderColor: palettes.brand.gray['500'],
    borderBottomLeftRadius: '4px',
    borderBottomRightRadius: '4px',
    boxShadow: '0px 1px 2px rgba(0,87,125,0.14), 0px 1px 1px rgba(0,87,125,0.1)'
  },
  suggestions: {
    marginTop: 0,
    listStyleType: 'none',
    paddingLeft: 0,
    '& li': {
      padding: '10px 15px',
      '&:hover': {
        backgroundColor: palettes.brand.blue.lightest,
        cursor: 'pointer'
      }
    },
    '& .suggestion-active': {
      backgroundColor: palettes.brand.blue.lightest
    }
  },
  noSuggestions: {
    padding: '10px 15px'
  },
  label: {
    display: 'block',
    fontSize: '0.87500rem',
    'font-weight': '700',
    padding: '0 0 0.25rem 1rem',
    margin: '0',
    color: palettes.brand.gray['700']
  },
  input: {
    outline: 'none',
    flex: '1 1 auto',
    minHeight: '32px',
    fontSize: '1.00000rem',
    border: 'none',
    color: palettes.brand.gray['800'],
    backgroundColor: 'transparent',
    width: '100%',
    minWidth: '0'
  }
}));

export const AutoComplete: FunctionComponent<AutocompleteProps> = ({
  id,
  label,
  value,
  onChange,
  isLoading = false,
  suggestions = [],
  onSelectOption,
  suggestionsLimit = SUGGESTIONS_LIMIT,
  showSuggestionsAfter = SHOW_SUGGESTIONS_AFTER,
  reset = false
}) => {
  /**
   * Styles
   */
  const classes = useStyles();

  const isAsync: boolean = !!onChange;

  /**
   * State
   */
  const [activeSuggestion, setActiveSuggestion] = useState<number>(0);
  const [filteredSuggestions, setFilteredSuggestions] = useState<AutocompleteProps['suggestions']>([]);
  const [showSuggestions, setShowSuggestions] = useState<boolean>(false);
  const [userInput, setUserInput] = useState<string>(value);

  useEffect(() => {
    setUserInput('');
  }, [reset]);

  const suggestionsList = isAsync ? suggestions : filteredSuggestions;

  const getSuggestions = useCallback(debounce(onChange ? onChange : () => {}, DEFAULT_EXECUTION_DELAY), []);

  const onChangeInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const userInput: string = event.currentTarget.value;
    setUserInput(userInput);

    if (userInput.length > showSuggestionsAfter) {
      setShowSuggestions(true);
      setActiveSuggestion(0);

      if (onChange) {
        return getSuggestions(userInput);
      }

      const filteredSuggestions: AutocompleteProps['suggestions'] = suggestions.filter(
        suggestion => suggestion.name.toLowerCase().indexOf(userInput.toLowerCase()) > -1
      );

      return setFilteredSuggestions(filteredSuggestions);
    }
  };

  const onClick = (selectedIndex: number) => {
    return () => {
      const selectedItem = suggestionsList[selectedIndex];
      setUserInput(selectedItem.name);
      onSelectOption(selectedItem);
      setShowSuggestions(false);
      setFilteredSuggestions([]);
      setActiveSuggestion(0);
    };
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.keyCode === KEY_PRESSED.ENTER) {
      setUserInput(suggestionsList[activeSuggestion]?.name);
      setShowSuggestions(false);
      setActiveSuggestion(0);
      onSelectOption(suggestionsList[activeSuggestion]);
    } else if (event.keyCode === KEY_PRESSED.ARROW_KEY_UP) {
      if (activeSuggestion === 0) {
        return;
      }
      setActiveSuggestion(activeSuggestion - 1);
    } else if (event.keyCode === KEY_PRESSED.ARROW_KEY_DOWN) {
      if (activeSuggestion - 1 === suggestionsList.length) {
        return;
      }

      setActiveSuggestion(activeSuggestion + 1);
    }
  };

  const renderSuggestionsList = (isLoading: boolean, suggestions: Array<SelectOption>) => {
    if (isLoading) {
      return (
        <div className={classes.noSuggestions}>
          <EllipsisLoader color={palettes.brand.blue.base} />
        </div>
      );
    }

    if (suggestions.length) {
      return (
        <ul className={classes.suggestions}>
          {suggestionsList.slice(0, suggestionsLimit).map((suggestion, index) => {
            return (
              <li
                className={index === activeSuggestion ? 'suggestion-active' : ''}
                key={suggestion.value}
                onClick={onClick(index)}
              >
                {suggestion.name}
              </li>
            );
          })}
        </ul>
      );
    }

    return (
      <div className={classes.noSuggestions}>
        <em>{i18next.t('noMatches')} ...</em>
      </div>
    );
  };

  return (
    <>
      <label id={`label-${id}`} htmlFor={id} className={classes.label}>
        {label}
      </label>
      <div className={classes.layout}>
        <input
          id={id}
          className={classes.input}
          type="text"
          onChange={onChangeInput}
          onKeyDown={onKeyDown}
          value={userInput}
        />
      </div>
      {showSuggestions && (
        <div className={classes.suggestionsList}>
          {renderSuggestionsList(isLoading, isAsync ? suggestions : filteredSuggestions)}
        </div>
      )}
    </>
  );
};
